import { useState, type ChangeEvent } from "react";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import type {
  IPagamentoLoja,
  IPedidoDetalhe,
  StatusPedido,
} from "interfaces/loja";
import {
  cancelarPedidoLojaOperacional,
  confirmarDevolucaoPedidoLoja,
  solicitarDevolucaoPedidoLoja,
} from "services/lojaPedidos";
import { setMessage } from "context";
import { mensagemSituacaoOperacional } from "utils/pedidoSituacaoOperacional";
import {
  resolveEstornoPedidoCanceladoUi,
  subtextoEstornoPedidoCancelado,
  type EstornoPedidoCanceladoUi,
} from "utils/pedidoEstornoUi";
import { Grid } from "@mui/material";
import PedidoInteligenciaOperacional from "./PedidoInteligenciaOperacional";
import {
  extrairPosVendaDoHistorico,
  labelTipoPosVenda,
} from "utils/pedidoPosVenda";

type AcaoLoading =
  | "cancelar"
  | "solicitarDevolucao"
  | "confirmarDevolucao"
  | null;

export type PedidoAcoesOperacionaisProps = {
  pedidoId: string;
  detalhe: IPedidoDetalhe;
  status: StatusPedido;
  pagamentos?: IPagamentoLoja[];
  /** Dispatch do `useMaterialUIController` (toasts). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: any;
  onPedidoRecarregado: () => Promise<void>;
};

function tipoAcaoParaStatus(status: StatusPedido): AcaoLoading | "none" {
  const s = String(status ?? "").toUpperCase();
  if (
    s === "AGUARDANDO_PAGAMENTO" ||
    s === "PAGO" ||
    s === "EM_PROCESSAMENTO"
  ) {
    return "cancelar";
  }
  if (s === "ENVIADO" || s === "ENTREGUE") return "solicitarDevolucao";
  if (s === "DEVOLUCAO_SOLICITADA") return "confirmarDevolucao";
  return "none";
}

function mensagemErroCancelar(code: string | undefined, fallback: string) {
  if (
    code === "PEDIDO_JA_ENVIADO_USE_DEVOLUCAO" ||
    code === "PEDIDO_JA_ENVIADO"
  ) {
    return "Este pedido já foi enviado: use Solicitar devolução em vez de cancelar.";
  }
  if (code === "PEDIDO_JA_ENTREGUE") {
    return "Pedido já entregue: o cancelamento direto não é permitido.";
  }
  return fallback;
}

function textoEstorno(ui: EstornoPedidoCanceladoUi): string {
  return ui === "pendente" ? "Estorno pendente" : "Estorno realizado";
}

function labelStatusPedidoChip(status: StatusPedido): string {
  const m: Record<string, string> = {
    AGUARDANDO_PAGAMENTO: "Aguardando pagamento",
    PAGO: "Pago",
    EM_PROCESSAMENTO: "Em processamento",
    PROCESSANDO: "Processando",
    ENVIADO: "Enviado",
    ENTREGUE: "Entregue",
    DEVOLUCAO_SOLICITADA: "Devolução solicitada",
    DEVOLVIDO: "Devolvido",
    CANCELADO: "Cancelado",
  };
  const k = String(status ?? "").toUpperCase();
  return m[k] ?? k.replace(/_/g, " ").toLowerCase();
}

export default function PedidoAcoesOperacionais({
  pedidoId,
  detalhe,
  status,
  pagamentos,
  dispatch,
  onPedidoRecarregado,
}: PedidoAcoesOperacionaisProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const acaoVisivel = tipoAcaoParaStatus(status);
  const [acaoLoading, setAcaoLoading] = useState<AcaoLoading>(null);

  const [modalCancelar, setModalCancelar] = useState(false);
  const [motivoCancelar, setMotivoCancelar] = useState("");

  const [modalDevolucao, setModalDevolucao] = useState(false);
  const [motivoDevolucao, setMotivoDevolucao] = useState("");

  const [confirmarDevolucaoOpen, setConfirmarDevolucaoOpen] = useState(false);

  const busy = acaoLoading !== null;

  const cancelado = String(status ?? "").toUpperCase() === "CANCELADO";
  const estornoUi = cancelado
    ? resolveEstornoPedidoCanceladoUi(pagamentos)
    : null;
  const estornoLabel = estornoUi ? textoEstorno(estornoUi) : null;
  const estornoHint = estornoUi
    ? subtextoEstornoPedidoCancelado(
        estornoUi,
        detalhe.total,
        pagamentos,
      )
    : null;

  const posVendaInfo = extrairPosVendaDoHistorico(detalhe.historicoPedidos);

  /** Mesmo acento visual da linha do tempo operacional (`PedidoTimelineOperacional`). */
  const accent = theme.palette.warning.main;
  const headerWash = alpha(accent, mode === "dark" ? 0.14 : 0.08);
  const borderSoft =
    mode === "dark" ? alpha("#c8e6c9", 0.35) : alpha(accent, 0.18);
  const headerDivider = alpha(theme.borders.borderColor, 0.6);

  async function aposSucesso(texto: string) {
    await onPedidoRecarregado();
    setMessage(dispatch, { open: true, severity: "success", text: texto });
  }

  function tratarErroCancelar(e: unknown) {
    const err = e as Error & { code?: string };
    const code = err.code;
    const hint = mensagemErroCancelar(
      code,
      err.message || "Não foi possível cancelar o pedido."
    );
    const severity =
      code === "PEDIDO_JA_ENVIADO_USE_DEVOLUCAO" || code === "PEDIDO_JA_ENVIADO"
        ? "warning"
        : "error";
    setMessage(dispatch, { open: true, severity, text: hint });
  }

  return (
    <>
      <Card
        id="sec-acoes-pedido"
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${borderSoft}`,
          overflow: "hidden",
          boxShadow:
            mode === "dark"
              ? `0 8px 32px ${alpha("#000", 0.35)}`
              : "0 8px 30px rgba(25, 118, 210, 0.08)",
        }}
      >
        <Stack spacing={0}>
          <Box
            sx={{
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 2, md: 2.25 },
              background: `linear-gradient(115deg, ${headerWash} 0%, ${alpha(
                accent,
                0.02
              )} 42%, transparent 72%)`,
              borderBottom: `1px solid ${headerDivider}`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(accent, 0.15),
                    color: accent,
                  }}
                >
                  <FactCheckOutlinedIcon sx={{ fontSize: 26 }} />
                </Box>
                <div>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight={700}
                  >
                    Operação
                  </Typography>
                  <Typography variant="h5" fontWeight={800} lineHeight={1.2}>
                    Ações do pedido
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={0.25}
                  >
                    Situação, estorno (quando cancelado) e operações de
                    cancelamento ou devolução
                  </Typography>
                </div>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="medium"
                  color="primary"
                  variant="outlined"
                  label={`Status: ${labelStatusPedidoChip(status)}`}
                  sx={{ fontWeight: 700, borderWidth: 1, padding: 2 }}
                />
                {posVendaInfo && String(status).toUpperCase() === "DEVOLUCAO_SOLICITADA" ? (
                  <>
                    <Chip
                      size="medium"
                      color={posVendaInfo.tipo === "TROCA" ? "info" : "warning"}
                      variant="filled"
                      label={labelTipoPosVenda(posVendaInfo.tipo)}
                      sx={{ fontWeight: 700 }}
                    />
                    <Chip
                      size="medium"
                      color={posVendaInfo.origemCliente ? "success" : "default"}
                      variant="outlined"
                      label={
                        posVendaInfo.origemCliente
                          ? "Solicitado pelo cliente"
                          : "Registrado pela loja"
                      }
                      sx={{ fontWeight: 600 }}
                    />
                  </>
                ) : null}
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 2, md: 2.5 },
              bgcolor: "background.paper",
            }}
          >
            <Stack spacing={2}>
              <Stack spacing={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    {/* Inteligência operacional */}
                    <PedidoInteligenciaOperacional
                      detalhe={detalhe}
                      pagamentos={pagamentos}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* Situação operacional */}
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight={700}
                    >
                      Situação operacional
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ lineHeight: 1.55, py: 0.5 }}
                    >
                      {mensagemSituacaoOperacional(status)}
                    </Typography>
                    {cancelado && estornoLabel ? (
                      <Alert
                        severity={
                          estornoUi === "pendente" ? "warning" : "success"
                        }
                        variant="outlined"
                        sx={{
                          py: 0.75,
                          bgcolor: alpha(theme.palette.background.paper, 0.85),
                        }}
                      >
                        <Typography variant="body2" fontWeight={700}>
                          {estornoLabel}
                        </Typography>
                        {estornoHint ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mt={0.5}
                          >
                            {estornoHint}
                          </Typography>
                        ) : null}
                      </Alert>
                    ) : null}
                  </Grid>
                </Grid>
              </Stack>

              {acaoVisivel !== "none" ? (
                <Stack spacing={1.25}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight={700}
                  >
                    Ações disponíveis
                  </Typography>
                  <MDTypography variant="caption" color="text" display="block">
                    Operações reais sobre o pedido (cancelamento e fluxo de
                    devolução).
                  </MDTypography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {acaoVisivel === "cancelar" ? (
                      <MDButton
                        color="error"
                        variant="gradient"
                        disabled={busy}
                        onClick={() => {
                          setMotivoCancelar("");
                          setModalCancelar(true);
                        }}
                      >
                        Cancelar Pedido
                      </MDButton>
                    ) : null}
                    {acaoVisivel === "solicitarDevolucao" ? (
                      <MDButton
                        color="warning"
                        variant="gradient"
                        disabled={busy}
                        onClick={() => {
                          setMotivoDevolucao("");
                          setModalDevolucao(true);
                        }}
                      >
                        Solicitar Devolução
                      </MDButton>
                    ) : null}
                    {acaoVisivel === "confirmarDevolucao" ? (
                      <MDButton
                        color="dark"
                        variant="gradient"
                        disabled={busy}
                        onClick={() => setConfirmarDevolucaoOpen(true)}
                      >
                        Confirmar Devolução
                      </MDButton>
                    ) : null}
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </Card>

      <Dialog
        open={modalCancelar}
        onClose={() => !busy && setModalCancelar(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancelar pedido</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" mb={2}>
            Confirme o cancelamento. O motivo é opcional.
          </MDTypography>
          <MDInput
            label="Motivo (opcional)"
            multiline
            rows={3}
            fullWidth
            value={motivoCancelar}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setMotivoCancelar(e.target.value)
            }
            disabled={busy}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            color="dark"
            variant="outlined"
            disabled={busy}
            onClick={() => setModalCancelar(false)}
          >
            Voltar
          </MDButton>
          <MDButton
            color="error"
            variant="gradient"
            disabled={busy}
            onClick={async () => {
              setAcaoLoading("cancelar");
              try {
                const motivo = motivoCancelar.trim() || undefined;
                await cancelarPedidoLojaOperacional(pedidoId, { motivo });
                setModalCancelar(false);
                await aposSucesso("Pedido cancelado com sucesso");
              } catch (e) {
                tratarErroCancelar(e);
              } finally {
                setAcaoLoading(null);
              }
            }}
          >
            Confirmar cancelamento
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modalDevolucao}
        onClose={() => !busy && setModalDevolucao(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Solicitar devolução</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" mb={2}>
            Registre a intenção de devolução. O motivo é opcional.
          </MDTypography>
          <MDInput
            label="Motivo (opcional)"
            multiline
            rows={3}
            fullWidth
            value={motivoDevolucao}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setMotivoDevolucao(e.target.value)
            }
            disabled={busy}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            color="dark"
            variant="outlined"
            disabled={busy}
            onClick={() => setModalDevolucao(false)}
          >
            Voltar
          </MDButton>
          <MDButton
            color="warning"
            variant="gradient"
            disabled={busy}
            onClick={async () => {
              setAcaoLoading("solicitarDevolucao");
              try {
                const motivo = motivoDevolucao.trim() || undefined;
                await solicitarDevolucaoPedidoLoja(pedidoId, { motivo });
                setModalDevolucao(false);
                await aposSucesso("Devolução solicitada");
              } catch (e) {
                const err = e as Error & { code?: string };
                setMessage(dispatch, {
                  open: true,
                  severity: "error",
                  text:
                    err.message || "Não foi possível solicitar a devolução.",
                });
              } finally {
                setAcaoLoading(null);
              }
            }}
          >
            Confirmar solicitação
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmarDevolucaoOpen}
        onClose={() => !busy && setConfirmarDevolucaoOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar devolução?</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            A devolução será confirmada e o pedido será cancelado conforme o
            fluxo da loja. Deseja continuar?
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            color="dark"
            variant="outlined"
            disabled={busy}
            onClick={() => setConfirmarDevolucaoOpen(false)}
          >
            Não
          </MDButton>
          <MDButton
            color="error"
            variant="gradient"
            disabled={busy}
            onClick={async () => {
              setAcaoLoading("confirmarDevolucao");
              try {
                await confirmarDevolucaoPedidoLoja(pedidoId);
                setConfirmarDevolucaoOpen(false);
                await aposSucesso("Devolução confirmada e pedido cancelado");
              } catch (e) {
                const err = e as Error & { code?: string };
                setMessage(dispatch, {
                  open: true,
                  severity: "error",
                  text:
                    err.message || "Não foi possível confirmar a devolução.",
                });
              } finally {
                setAcaoLoading(null);
              }
            }}
          >
            Sim, confirmar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
